import * as React from 'react';

import { Button, cn, TextField } from '@rematter/ui-web';
import { ArrowLeftRight } from 'lucide-react';

import { SelectMaterial } from '~/shared/components/select-fields/SelectMaterial';

type DescriptionCellProps = {
  value: string;
  itemId: string | null;
  onChange: (description: string, itemId: string | null) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  selectPlaceholder?: string;
  textPlaceholder?: string;
  className?: string;
};

export const DescriptionCell: React.FC<DescriptionCellProps> = ({
  value,
  itemId,
  onChange,
  isDisabled = false,
  isInvalid,
  errorMessage,
  selectPlaceholder,
  textPlaceholder,
  className,
}) => {
  const [mode, setMode] = React.useState<'select' | 'text'>(
    itemId ? 'select' : 'text',
  );

  const savedSelectState = React.useRef<{
    itemId: string | null;
    description: string;
  } | null>(null);

  const handleModeToggle = React.useCallback(() => {
    if (mode === 'select') {
      savedSelectState.current = { itemId, description: value };
      setMode('text');
    } else {
      const saved = savedSelectState.current;
      if (saved) {
        onChange(saved.description, saved.itemId);
        savedSelectState.current = null;
      }
      setMode('select');
    }
  }, [mode, itemId, value, onChange]);

  const handleSelectChange = React.useCallback(
    (
      newItemId: string | undefined,
      entity?: { id: string; name: string } | undefined,
    ) => {
      onChange(entity?.name ?? '', newItemId ?? null);
    },
    [onChange],
  );

  const handleTextChange = React.useCallback(
    (newValue: string) => {
      savedSelectState.current = null;
      onChange(newValue, null);
    },
    [onChange],
  );

  return (
    <div
      className={cn('@DescriptionCell', 'flex items-center gap-1', className)}
    >
      <div className="min-w-0 flex-1">
        {mode === 'select' ? (
          <SelectMaterial
            value={itemId ?? undefined}
            onChange={handleSelectChange}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            errorMessage={errorMessage}
            placeholder={selectPlaceholder}
          />
        ) : (
          <TextField
            value={value}
            onChange={handleTextChange}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            errorMessage={errorMessage}
            placeholder={textPlaceholder}
          />
        )}
      </div>
      <Button
        onClick={handleModeToggle}
        isDisabled={isDisabled}
        variant="outline"
        color="neutral"
        icon={<ArrowLeftRight className="size-4 text-neutral/60" />}
      />
    </div>
  );
};
